import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchDestinationTransfers,
  createDestinationTransfer,
  updateDestinationTransfer,
  deleteDestinationTransfer,
  upsertDestinationTransfer
} from '../../services/destinationTransfers';

export const getDestinationTransfersThunk = createAsyncThunk(
  'destinationTransfers/getDestinationTransfers',
  async (_, thunkAPI) => {
    try {
      const data = await fetchDestinationTransfers();
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const addDestinationTransferThunk = createAsyncThunk(
  'destinationTransfers/addDestinationTransfer',
  async (transferData, thunkAPI) => {
    try {
      const newTransfer = await createDestinationTransfer(transferData);
      return newTransfer;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const editDestinationTransferThunk = createAsyncThunk(
  'destinationTransfers/editDestinationTransfer',
  async ({ id, data }, thunkAPI) => {
    try {
      const updated = await updateDestinationTransfer(id, data);
      return updated;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const upsertDestinationTransferThunk = createAsyncThunk(
  'destinationTransfers/upsertDestinationTransfer',
  async (transferData, thunkAPI) => {
    try {
      const updated = await upsertDestinationTransfer(transferData);
      return updated;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const removeDestinationTransferThunk = createAsyncThunk(
  'destinationTransfers/removeDestinationTransfer',
  async (id, thunkAPI) => {
    try {
      await deleteDestinationTransfer(id);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

const initialState = {
  transfers: [],
  status: 'idle',
  error: null,
  lastFetched: null,
};

export const destinationTransferSlice = createSlice({
  name: 'destinationTransfers',
  initialState,
  reducers: {
    resetStatus: (state) => {
      state.status = 'idle';
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDestinationTransfersThunk.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getDestinationTransfersThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.transfers = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(getDestinationTransfersThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.error || 'Error al cargar traslados';
      })
      .addCase(addDestinationTransferThunk.fulfilled, (state, action) => {
        state.transfers.unshift(action.payload);
      })
      .addCase(editDestinationTransferThunk.fulfilled, (state, action) => {
        const index = state.transfers.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.transfers[index] = action.payload;
        }
      })
      .addCase(upsertDestinationTransferThunk.fulfilled, (state, action) => {
        const item = action.payload;
        const index = state.transfers.findIndex(t =>
          t.id === item.id || (
            String(t.place_id) === String(item.place_id) &&
            String(t.provider_id) === String(item.provider_id) &&
            t.route_type === item.route_type &&
            t.transfer_mode === item.transfer_mode
          )
        );
        if (index !== -1) {
          state.transfers[index] = item;
        } else {
          state.transfers.unshift(item);
        }
      })
      .addCase(removeDestinationTransferThunk.fulfilled, (state, action) => {
        state.transfers = state.transfers.filter(t => t.id !== action.payload);
      });
  },
});

export const { resetStatus } = destinationTransferSlice.actions;
export const destinationTransfersSelector = (state) => state.destinationTransfers?.transfers || [];

export default destinationTransferSlice.reducer;
